import React, { useState, useRef, useEffect } from "react";
import { DialogContainer } from "react-md";
import morningIcon from "../../../../../../assets/icons/time_morning.svg";
import afternoonIcon from "../../../../../../assets/icons/time_afternoon.svg";
import eveningIcon from "../../../../../../assets/icons/time_evening.svg";
import nightIcon from "../../../../../../assets/icons/time_night.svg";

// Time-of-day icons shown before each bucket title (matches the patient app).
const BUCKET_ICONS = {
  Morning: morningIcon,
  Afternoon: afternoonIcon,
  Evening: eveningIcon,
  Night: nightIcon,
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Minutes-of-day (e.g. 600) → "10:00 AM".
const minutesToTime = (mins) => {
  if (mins == null) return "";
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// ISO date string → "10 Apr 2026" (UTC, to match date-only dayDate values).
const formatReqDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).substring(0, 10);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// Time-of-day bucketing — mirrors the Flutter app's Morning / Afternoon /
// Evening / Night grouping (getPartOfDay). Input is minutes-of-day (slot.value).
//   12:00 – 11:59 AM → Morning
//   12:00 – 3:59 PM  → Afternoon
//   4:00 – 7:59 PM   → Evening
//   ≥ 8:00 PM        → Night
const BUCKETS = ["Morning", "Afternoon", "Evening", "Night"];
const bucketOf = (mins) => {
  if (mins == null) return "Morning";
  if (mins >= 0 && mins < 720) return "Morning"; // 12 AM – 11:59 AM
  if (mins >= 720 && mins < 960) return "Afternoon"; // 12 PM – 3:59 PM
  if (mins >= 960 && mins < 1200) return "Evening"; // 4 PM – 7:59 PM
  return "Night"; // ≥ 8 PM
};

const SelectDateSlotSection = (props) => {
  const {
    openSelectDateSlot,
    setOpenSelectDateSlot,
    slotData = { dates: [], slots: {} },
    onConfirm,
    // The slot currently stored on the cart (any mode). Used for both
    // default selection and the optional banner. Shape: { date, time, id }.
    requestedSlot = null,
    // Show the "Patient requested ..." banner. Bridge passes true only in
    // VERIFY mode; CREATE/EDIT-DRAFT silently uses requestedSlot as the
    // default-selection seed without flagging it as a patient ask.
    showRequestedBanner = false,
    // If the cart's stored slot isn't available (or no requestedSlot was
    // provided), fall back to selecting the first date that has slots.
    // True for DRAFT carts (CREATE / EDIT-DRAFT); false for VERIFY so we
    // honour "select nothing when the patient's date is gone".
    autoSelectFirstAvailable = false,
  } = props;

  const dates = slotData.dates || [];
  const slotsMap = slotData.slots || {};

  // Is the patient's originally-requested slot still available in the fetched grid?
  let requestedInfo = null;
  if (showRequestedBanner && requestedSlot && requestedSlot.date) {
    const reqDateStr = String(requestedSlot.date).substring(0, 10);
    const dateIdx = dates.findIndex((d) => d.dayDate === reqDateStr);
    let available = false;
    let matchedTime = "";
    if (dateIdx >= 0) {
      const slotsForDate = slotsMap[dateIdx] || [];
      const matchSlot = slotsForDate.find(
        (s) =>
          (requestedSlot.id != null && s.key === requestedSlot.id) ||
          s.value === requestedSlot.time,
      );
      available = !!matchSlot;
      // Prefer the matched slot's range label so the banner reads the same as
      // the chip below it ("10:00 - 10:30 AM", not "10:00 AM"). Only the stored
      // start time survives on the cart, so an unavailable slot has no label to
      // borrow and falls back to the start time.
      matchedTime = matchSlot?.time || "";
    }
    requestedInfo = {
      label: `${formatReqDate(requestedSlot.date)} at ${
        matchedTime || minutesToTime(requestedSlot.time)
      }`,
      available,
    };
  }

  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [showError, setShowError] = useState(false);
  // Which time-of-day buckets are expanded. Object so spread updates are
  // cheap; absent key = collapsed. Reset on date change so each new date
  // starts fresh (with auto-open below).
  const [openBuckets, setOpenBuckets] = useState({});
  const scrollContainerRef = useRef(null);
  // Auto-select runs once per dialog opening. The ref guards against
  // re-running when dates/slotsMap identities churn during render but the
  // dialog is still on the same open.
  const autoSelectedRef = useRef(false);

  // Get slots for currently selected date
  const currentSlots =
    selectedDateIndex !== null && slotsMap[selectedDateIndex]
      ? slotsMap[selectedDateIndex]
      : [];

  const isDateDisabled = (index) => {
    const dateSlots = slotsMap[index];
    return !dateSlots || dateSlots.length === 0;
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheel = (event) => {
      if (event.deltaY !== 0) {
        event.preventDefault();
        scrollContainer.scrollLeft += event.deltaY;
      }
    };

    scrollContainer.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      scrollContainer.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Auto-select date (and slot, if requestedSlot's id/time matches an
  // available slot) when the dialog opens. Rules:
  //   - cart has a stored slot AND it's available → select that date+slot
  //   - cart has a stored slot but it's NOT available, autoSelectFirstAvailable
  //       is true → fall back to the first date that has slots
  //   - cart has NO stored slot AND autoSelectFirstAvailable → first available
  //   - otherwise → leave both null (user picks manually)
  useEffect(() => {
    if (!openSelectDateSlot) {
      autoSelectedRef.current = false;
      setSelectedDateIndex(null);
      setSelectedSlotIndex(null);
      setOpenBuckets({});
      return;
    }
    if (autoSelectedRef.current) return;
    if (!dates || dates.length === 0) return;

    if (requestedSlot && requestedSlot.date) {
      const reqDateStr = String(requestedSlot.date).substring(0, 10);
      const dateIdx = dates.findIndex((d) => d.dayDate === reqDateStr);
      if (dateIdx >= 0 && !isDateDisabled(dateIdx)) {
        setSelectedDateIndex(dateIdx);
        const slotsForDate = slotsMap[dateIdx] || [];
        const slotIdx = slotsForDate.findIndex(
          (s) =>
            (requestedSlot.id != null && s.key === requestedSlot.id) ||
            s.value === requestedSlot.time,
        );
        if (slotIdx >= 0) {
          setSelectedSlotIndex(slotIdx);
        }
        autoSelectedRef.current = true;
        return;
      }
      // Stored date is unavailable. For non-DRAFT (VERIFY) — honour "select
      // nothing". For DRAFT, fall through to the first-available branch.
      if (!autoSelectFirstAvailable) {
        autoSelectedRef.current = true;
        return;
      }
    }

    if (autoSelectFirstAvailable) {
      const firstAvailable = dates.findIndex((_, i) => !isDateDisabled(i));
      if (firstAvailable >= 0) {
        setSelectedDateIndex(firstAvailable);
      }
      autoSelectedRef.current = true;
    }
  }, [openSelectDateSlot, dates, slotsMap, requestedSlot, autoSelectFirstAvailable]);

  const handleDateClick = (index) => {
    if (isDateDisabled(index)) return;
    setSelectedDateIndex(index);
    // Manual date change clears any prior slot selection — slot indexes are
    // per-date and would otherwise point at the wrong slot. (The auto-select
    // effect updates both together so it doesn't go through this path.)
    setSelectedSlotIndex(null);
    setOpenBuckets({});
  };

  const toggleBucket = (bucket) => {
    setOpenBuckets((prev) => ({ ...prev, [bucket]: !prev[bucket] }));
  };

  // Auto-open the bucket the user most likely wants to see: the one
  // containing the currently selected slot (e.g. cart's stored slot was
  // restored by the auto-select effect), else the first non-empty bucket.
  // Only nudges open state when no buckets are currently open — once the
  // user has manually toggled, we leave their choice alone.
  useEffect(() => {
    if (selectedDateIndex === null) return;
    const allClosed = Object.values(openBuckets).every((v) => !v);
    if (!allClosed) return;
    const slots = slotsMap[selectedDateIndex] || [];
    if (slots.length === 0) return;
    let bucketToOpen = null;
    if (selectedSlotIndex !== null && slots[selectedSlotIndex]) {
      bucketToOpen = bucketOf(slots[selectedSlotIndex].value);
    } else {
      for (const b of BUCKETS) {
        if (slots.some((s) => bucketOf(s.value) === b)) {
          bucketToOpen = b;
          break;
        }
      }
    }
    if (bucketToOpen) {
      setOpenBuckets({ [bucketToOpen]: true });
    }
  }, [selectedDateIndex, selectedSlotIndex, slotsMap]);

  const handleContinue = () => {
    if (selectedDateIndex === null || selectedSlotIndex === null) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const selectedDate = dates[selectedDateIndex];
    const selectedSlot = currentSlots[selectedSlotIndex];
    if (onConfirm) {
      onConfirm(selectedDate, selectedSlot);
    } else {
      setOpenSelectDateSlot(false);
    }
  };

  const isContinueDisabled =
    selectedDateIndex === null || selectedSlotIndex === null;

  // Pre-bucket currentSlots by time-of-day so each bucket section can render
  // a sub-grid. We preserve each slot's ORIGINAL index in currentSlots so the
  // selectedSlotIndex / onClick still address the same slot identity even
  // though the visible order is now grouped.
  const bucketedSlots = BUCKETS.map((bucket) => ({
    bucket,
    items: currentSlots
      .map((slot, i) => ({ slot, i }))
      .filter(({ slot }) => bucketOf(slot.value) === bucket),
  })).filter((b) => b.items.length > 0);

  return (
    <DialogContainer
      id="cart-select-date-slot-dialog"
      visible={openSelectDateSlot}
      focusOnMount={false}
      onHide={() => {
        setOpenSelectDateSlot(false);
      }}
      width={"1000px"}
      minWidth={"600px"}
      autosizeContent={true}
      dialogStyle={{ borderRadius: "10px" }}
    >
      <div className="sds-container">
        {/* Header */}
        <div className="sds-header">
          <h2 className="sds-title">Select appointment slots</h2>
          <span
            className="sds-close-btn"
            onClick={() => setOpenSelectDateSlot(false)}
          >
            &times;
          </span>
        </div>

        {/* Requested-slot availability note (VERIFY mode) */}
        {requestedInfo && (
          <div
            style={{
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "12px",
              fontSize: "14px",
              background: requestedInfo.available ? "#e6f4ea" : "#fdeaea",
              color: requestedInfo.available ? "#1e7e34" : "#b3261e",
              border: `1px solid ${
                requestedInfo.available ? "#bfe3c9" : "#f3c3c0"
              }`,
            }}
          >
            {requestedInfo.available
              ? `Patient requested ${requestedInfo.label} — still available. You can re-select it or pick another slot.`
              : `Patient requested ${requestedInfo.label} — no longer available. Please choose an available slot.`}
          </div>
        )}

        {/* Error Banner */}
        {showError && (
          <div className="sds-error-banner">
            Requested appointment slots are not available, please choose
            available slots
          </div>
        )}

        {/* Available Dates */}
        <div className="sds-section">
          <h4 className="sds-section-title">Available dates</h4>
          <div className="sds-dates-scroll" ref={scrollContainerRef}>
            {dates.map((date, index) => {
              const disabled = isDateDisabled(index);
              return (
                <div
                  key={index}
                  className={`sds-date-chip ${
                    selectedDateIndex === index ? "sds-date-chip--selected" : ""
                  } ${disabled ? "sds-date-chip--disabled" : ""}`}
                  onClick={() => handleDateClick(index)}
                >
                  <span className="sds-date-month">{date.month}</span>
                  <span className="sds-date-day">{date.day}</span>
                  <span className="sds-date-weekday">{date.weekday}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Available Slots */}
        <div className="sds-section sds-section--slots">
          <h4 className="sds-section-title">Available slots</h4>
          {selectedDateIndex !== null ? (
            <div className="sds-slots-scroll">
              {bucketedSlots.length === 0 ? (
                <div style={{ color: "#999", fontSize: "13px" }}>
                  No slots for this date
                </div>
              ) : (
                bucketedSlots.map(({ bucket, items }) => {
                  const isOpen = !!openBuckets[bucket];
                  return (
                    <div
                      key={bucket}
                      className={`sds-bucket ${
                        isOpen ? "sds-bucket--open" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="sds-bucket-header"
                        onClick={() => toggleBucket(bucket)}
                        aria-expanded={isOpen}
                      >
                        {BUCKET_ICONS[bucket] && (
                          <img
                            src={BUCKET_ICONS[bucket]}
                            alt=""
                            className="sds-bucket-icon"
                          />
                        )}
                        <span className="sds-bucket-title">{bucket}</span>
                        {/* The "N times" count (number of chips in this bucket,
                            not a slot's own availableCount) is hidden — the
                            chips below already say it. The title now carries the
                            flex-grow that used to push the chevron right. */}
                        <i className="material-icons sds-bucket-chevron">
                          {isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                        </i>
                      </button>
                      {isOpen && (
                        <div className="sds-slots-grid">
                          {items.map(({ slot, i }) => {
                            const selected = selectedSlotIndex === i;
                            return (
                              <div
                                key={i}
                                className={`sds-slot-chip ${
                                  selected ? "sds-slot-chip--selected" : ""
                                }`}
                                onClick={() => setSelectedSlotIndex(i)}
                              >
                                <span className="sds-slot-time">
                                  {slot.time || minutesToTime(slot.value)}
                                </span>
                                {slot.availableCount > 0 && (
                                  <span className="sds-slot-count">
                                    {slot.availableCount} slot
                                    {slot.availableCount === 1 ? "" : "s"}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div style={{ color: "#999", fontSize: "13px", padding: "10px 0" }}>
              Please select a date first
            </div>
          )}
        </div>

        {/* Continue Button */}
        <div className="sds-footer">
          <button
            className={`sds-continue-btn ${
              isContinueDisabled ? "sds-continue-btn--disabled" : ""
            }`}
            onClick={handleContinue}
            disabled={isContinueDisabled}
          >
            Continue
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default SelectDateSlotSection;
